import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  deletePromocodeItem,
  getPromocodesList,
  handlePaginate,
  handleSelectAll,
  handleSelectItem,
  updatePromocodeBunch,
} from "store/liveOps/promocodes/slice";
import { promocodesListLimits } from "configs/limits";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";

import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Pagination from "components/Pagination";
import ActionBtn from "components/buttons/ActionBtn";
import PromocodeFilter from "./PromocodeFilter";
import {
  NotificationTypes,
  showNotification,
} from "../../../store/notifications/slice";
import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { RefreshButton } from "components/buttons/RefreshButton";
import { useRefreshList } from "helpers/useRefreshList";

const PromocodesList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [order, setOrder] = useState<{
    title: string;
    isActive: string;
    code: string;
    maxUsedCount: string;
    currentUsedCount: string;
    id: string;
  }>({
    title: "none",
    isActive: "none",
    code: "none",
    maxUsedCount: "none",
    currentUsedCount: "none",
    id: "none",
  });
  const [currentLimit, setLimit] = useState<number>(promocodesListLimits[0]);
  const [search, setSearch] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [active, setActive] = useState<
    { value: number | string; label: string }[]
    >([{ value: 3, label: "All" }]);
  

  const options = [
    {
      label: t("activate"),
      value: "activate",
    },
    {
      label: t("hide"),
      value: "hide",
    },
  ];

  const list = useAppSelector((store) => store.promocodes.list);
  const isListFetched = useAppSelector(
    (store) => store.promocodes.isListFetched,
  );
  const totalItems = useAppSelector((store) => store.promocodes.totalItems);
  const totalPages = useAppSelector((store) => store.promocodes.totalPages);
  const activePage = useAppSelector((store) => store.promocodes.activePage);
  const selectedItems = useAppSelector(
    (store) => store.promocodes.selectedItems,
  );
  const processingItemId = useAppSelector(
    (store) => store.promocodes.processingItemId,
  );

  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'promocodes',
    isListFetched
  });

 const promoTypeMap: Record<string, string> = {
   bonuses: "bonus",
   cashbacks: "cashback",
   deposits: "deposit",
   tournaments: "tournament",
 };
 const currentTab = location.pathname.split("/").pop() || "";
  const promoType = promoTypeMap[currentTab];
  const permissions = usePermissionHelper();

  const actionProps = permissions?.update
  ? {
      onEdit: (id: number) => handleOpenEditModal(id),
      onView: undefined,
    }
  : {
      onEdit: undefined,
      onView: (id: number) => handleOpenEditModal(id),
    };

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getPromocodesList({
          limit: currentLimit,
          offset: activePage - 1,
          type: promoType,
          isActive:
            active[0] && active[0].value !== 3
              ? (active[0].value as number)
              : undefined,
          title: search,
          code: code,
          order,
        }),
      );
    }
  }, [isListFetched, activePage, currentLimit, order]);

  const handleAllClick = () => {
    dispatch(handleSelectAll());
  };
  const handleItemClick = (id: number) => {
    dispatch(handleSelectItem(id));
  };
  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleActionClick = async (e: string) => {
    try {
      await dispatch(
        updatePromocodeBunch({
          ids: selectedItems,
          isActive: e === "activate" ? 1 : 0,
        }),
      );
    } catch (error) {
      console.log(error, "error");
    }
  };

  const handleOpenEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.promoCode}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.promoCode }));
  };
  const handleOpenCreateModal = () => {
    navigate(`${location.pathname}?activeModal=${ModalTypes.promoCode}`);
    dispatch(openModal({ activeModal: ModalTypes.promoCode }));
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];
    let next: "asc" | "desc" | "none";

    if (current === "none") next = "asc";
    else if (current === "asc") next = "desc";
    else next = "none";

    const newOrder = {
      [key]: next,
      ...Object.fromEntries(Object.entries(order).filter(([k]) => k !== key)),
    };
    // @ts-ignore
    setOrder(newOrder);
    dispatch(handlePaginate(1));
  };

  const handleDeleteItem = async (id: number) => {
    const result = await dispatch(deletePromocodeItem(id ?? 0));
    if (result?.payload === true) {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
      dispatch(
        getPromocodesList({
          limit: currentLimit,
          offset: activePage - 1,
          type: promoType,
          isActive:
            active[0] && active[0].value !== 3
              ? (active[0].value as number)
              : undefined,
          title: search,
          code: code,
          order,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <PromocodeFilter
        handleApply={() => {
          dispatch(handlePaginate(1));
        }}
        searchValue={search}
        handleCode={setCode}
        codeValue={code}
        handleSearch={setSearch}
        selectedActive={active}
        handleUpdateActive={setActive}
      />
      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box">
            {permissions?.create && (
              <ActionBtn
                txt={t("addPromocode")}
                handleClick={() => {
                  handleOpenCreateModal();
                }}
              />
            )}
          </div>
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
          <div className="page-list-header--box">
            <DefaultDropdown
              onSelect={(e) => {
                handleActionClick(e as string);
              }}
              placeholder={t("chooseAction")}
              options={options}
              isSmall
              disabled={selectedItems.length === 0 || !permissions?.update}
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={8} rowsCount={3} />
            <PaginationSkeleton />
          </>
        )}

        {isListFetched && (
          <>
            <div className="game-table-wrap">
              {list.length === 0 && (
                <EmptyState message={t("noPromocodesFound")} />
              )}

              {list.length > 0 && (
                <Table
                  data={list}
                  order={order}
                  columns={{
                    sortingColumns: {
                      maxUsedCount: {
                        value: t("useCount"),
                        hasSorting: true,
                      },
                      currentUsedCount: {
                        value: t("used"),
                        hasSorting: true,
                      },
                      isActive: {
                        value: t("active"),
                      },
                    },
                    hiddenColumns: ["maxUsesPerUser", "startsAt", "endsAt"],
                  }}
                  idField="id"
                  selectedItems={selectedItems}
                  onAllSelect={handleAllClick}
                  onSelect={(id) => handleItemClick(id)}
                  onDelete={(id) => handleDeleteItem(id)}
                  onSort={(id) => (id ? handleOrderClick(id) : {})}
                  processingId={processingItemId}
                  {...actionProps}
                  hasDelete={permissions?.delete}
                  hasEdit={permissions?.update}
                  hasView={permissions?.read && !permissions?.update}
                  hasOptions={permissions?.read || permissions?.update}
                />
              )}
            </div>

            {list.length > 0 && (
              <Pagination
                handleLimitClick={(e) => {
                  setLimit(e);
                  dispatch(handlePaginate(1));
                }}
                limitsArray={promocodesListLimits}
                activePage={activePage}
                limit={currentLimit}
                totalItems={totalItems}
                totalPages={totalPages}
                handleNextClick={handleNextClick}
                handlePrevClick={handlePrevClick}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PromocodesList;
